/**
 * Handles the transformation and configuration of search functionality.
 * - Provides search section configurations for different content types (Programs, Series, Movies)
 * - Transforms Search Api results into correct format
 */

import { PageSection, PageSectionCustomAttributes } from '../types/Page';
import { ModuleType, StripeVarietyGS } from '../types/Common';
import { SearchData, SearchDatasArray } from '../types/Search';

export type SearchSectionType = 'PROGRAM' | 'PROGRAM_SERIES' | 'VOD_ASSET' | 'VOD_SERIES';

export interface SearchSection extends Omit<PageSection, 'customAttributes'> {
    id: string;
    title: string;
    collectionId: string | null;
    expandPageId: string | null;
    expandable: boolean;
    imageAspectRatio: string;
    customAttributes: PageSectionCustomAttributes & {
        type: SearchSectionType;
    };
}

const SEARCH_ITEMS_PER_PAGE = 50;

const createSearchSection = (
    id: string,
    title: string,
    type: SearchSectionType,
    collectionId?: string | null,
    aspectRatio?: string,
    totalCount = 0
): SearchSection => ({
    id: `search-${id}`,
    title,
    collectionId: collectionId || null,
    expandPageId: null,
    expandable: Boolean(collectionId) && totalCount > SEARCH_ITEMS_PER_PAGE,
    imageAspectRatio: aspectRatio || '16:9',
    channelGroupId: null,
    customAttributes: {
        variant: 'a1',
        variety: StripeVarietyGS.STRIPE,
        moduleType: ModuleType.MIXED,
        itemPerPage: SEARCH_ITEMS_PER_PAGE,
        type,
        hideTitle: false,
    },
});

export function getSearchSections(): SearchSection[] {
    return [
        createSearchSection('vod-assets', 'Movies', 'VOD_ASSET'),
        createSearchSection('program-series', 'Series', 'PROGRAM_SERIES'),
        createSearchSection('vod-series', 'VOD Series', 'VOD_SERIES'),
        createSearchSection('program', 'Program', 'PROGRAM'),
    ];
}

function getTopRank(response: SearchData): number {
    if (!response?.results?.length) {
        return -Infinity;
    }

    return response.results.reduce((max, item) => {
        const rank = typeof item?.rank === 'number' ? item.rank : -Infinity;
        return Math.max(rank, max);
    }, -Infinity);
}

export function getSearchSectionsFromData(searchData: SearchDatasArray, pageSections: PageSection[] = []): SearchSection[] {
    const sections: SearchSection[] = [];

    const orderedData = searchData
        .map((response, index) => ({ response, index }))
        .sort((a, b) => getTopRank(b.response) - getTopRank(a.response) || a.index - b.index)
        .map((entry) => entry.response);

    orderedData.forEach((response) => {
        if (response && response.count > 0) {
            const firstResult = response.results?.[0];
            const sectionType = (firstResult?.type as SearchSectionType) || 'VOD_ASSET';

            const matchingSection = pageSections.find(
                (section) =>
                    section.collectionId && (section.collectionId === response.id || section.collectionId === response.collection_id)
            );

            sections.push(
                createSearchSection(
                    response.id || `section-${sections.length}`,
                    matchingSection?.title ?? response.title,
                    sectionType,
                    response.id,
                    response.image_aspect_ratio || '16:9',
                    response.count
                )
            );
        }
    });

    return sections;
}

export function isEmptySearchResults(searchResponses: SearchDatasArray): boolean {
    if (!searchResponses || !searchResponses.length) {
        return true;
    }

    const hasResults = searchResponses.some((response) => response && response.results && response.results.length > 0);

    return !hasResults;
}

export function findSearchResponseWithType(searchResponses: SearchDatasArray, type: SearchSectionType): SearchData | null {
    if (!searchResponses || !searchResponses.length) {
        return null;
    }

    return searchResponses.find((response) => response.results && response.results.some((item) => item.type === type)) || null;
}
